import { formatCurrency, formatDateTime } from '@crm/shared';
import { Badge, Card, CardContent, Spinner } from '@crm/ui';
import { ExternalLink, Receipt } from 'lucide-react';
import { EmptyState, Pagination } from '@/components';
import type { BillingOrder, BillingOrderStatus, BillingOrders } from '../api';

const SITUACOES: Record<BillingOrderStatus, { label: string; tom: string }> = {
  PAID: { label: 'Pago', tom: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
  PENDING: { label: 'Em aberto', tom: 'bg-amber-500/15 text-amber-700 dark:text-amber-400' },
  CANCELLED: { label: 'Cancelado', tom: 'bg-destructive/15 text-destructive' },
};

function itens(order: BillingOrder): string {
  const partes = [
    order.planName ? `Plano ${order.planName}` : null,
    order.lineAmount > 0 ? 'Linha de WhatsApp' : null,
    order.messages > 0 ? `${order.messages.toLocaleString('pt-BR')} mensagens` : null,
  ].filter(Boolean);

  return partes.length === 0 ? 'Cobranca' : partes.join(' + ');
}

function Situacao({ order }: { order: BillingOrder }) {
  const situacao = SITUACOES[order.status];

  return (
    <Badge variant="secondary" className={situacao.tom}>
      {situacao.label}
    </Badge>
  );
}

function Fatura({ order }: { order: BillingOrder }) {
  if (order.status !== 'PENDING' || !order.checkoutUrl) return null;

  return (
    <a
      href={order.checkoutUrl}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
    >
      <ExternalLink className="h-3.5 w-3.5" />
      Pagar
    </a>
  );
}

interface PaymentsListProps {
  orders?: BillingOrders;
  isLoading: boolean;
  onPageChange: (page: number) => void;
}

export function PaymentsList({ orders, isLoading, onPageChange }: PaymentsListProps) {
  const linhas = orders?.data ?? [];

  return (
    <div className="space-y-4">
      <Card variant="flush">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : linhas.length === 0 ? (
            <div className="py-8">
              <EmptyState icon={<Receipt />} title="Nenhum pagamento ainda" />
            </div>
          ) : (
            <>
              <ul className="divide-y divide-border md:hidden">
                {linhas.map((order) => (
                  <li key={order.id} className="space-y-2 px-4 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium">{formatCurrency(order.total)}</span>
                      <Situacao order={order} />
                    </div>
                    <p className="text-sm text-muted-foreground">{itens(order)}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(order.createdAt)}
                    </p>
                    <Fatura order={order} />
                  </li>
                ))}
              </ul>

              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-medium">Quando</th>
                      <th className="px-4 py-3 font-medium">O que</th>
                      <th className="px-4 py-3 text-right font-medium">Valor</th>
                      <th className="px-4 py-3 font-medium">Situacao</th>
                      <th className="px-4 py-3 font-medium">Fatura</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((order) => (
                      <tr key={order.id} className="border-t border-border">
                        <td className="whitespace-nowrap px-4 py-3">
                          {formatDateTime(order.createdAt)}
                        </td>
                        <td className="px-4 py-3">{itens(order)}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums font-medium">
                          {formatCurrency(order.total)}
                        </td>
                        <td className="px-4 py-3">
                          <Situacao order={order} />
                        </td>
                        <td className="px-4 py-3">
                          <Fatura order={order} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Pagination meta={orders?.meta} label="pagamentos" onChange={onPageChange} />
    </div>
  );
}
